/**
 * Pure-drawing helpers for the race track scene. Split out of track.ts to
 * keep the orchestrator scene file lean — track.ts focuses on per-frame
 * state + DOM overlays, this file owns the canvas pixels.
 *
 * Visual upgrade (Phase 2):
 *   - Stadium background: vertical gradient + horizon line + crowd silhouette.
 *   - Track: rounded outer rail, packed-dirt lanes (subtle grain), white
 *     dashed lane dividers, distance markers, start gate boxes, alternating
 *     black/white checkered finish line.
 *   - Horses: 3/4 side-view sprites with a 4-frame procedural gallop
 *     animation (ground reach, push, suspended, recovery). Silks fill the
 *     body; jockey wears matching helmet.
 *
 * Visual upgrade (Phase 4 — OpenArt sprite pack):
 *   - Bitmap horses come from a pre-baked multi-row sprite sheet (one PNG
 *     per coat/saddle variant, 18 in total). Each sheet has a 64px row
 *     height + 80px frame stride. We use row 0 (idle, 11 frames) and row 4
 *     (run, 6 frames). 8 lane variants are picked deterministically from
 *     the 18-file pool — no runtime tinting.
 *   - If no variant loads, the procedural draw stays in service.
 */

import type { TrackHorse } from '../state/types.js';
import { getSpriteFrame, animFrameCount, type AnimName } from './horse-sprites.js';
import { drawProceduralHorse, HORSE_LEN, HORSE_THICK } from './horse-procedural.js';

export interface TrackLayout {
  trackX0: number;
  trackX1: number;
  trackY0: number;
  laneH: number;
  numLanes: number;
}

export interface RectArgs {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

export function roundedRect(c: CanvasRenderingContext2D, args: RectArgs): void {
  const rr = Math.min(args.r, args.w / 2, args.h / 2);
  const { x, y, w, h } = args;
  c.beginPath();
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.quadraticCurveTo(x + w, y, x + w, y + rr);
  c.lineTo(x + w, y + h - rr);
  c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  c.lineTo(x + rr, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - rr);
  c.lineTo(x, y + rr);
  c.quadraticCurveTo(x, y, x + rr, y);
  c.closePath();
}

export function rgbaFromHex(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Paint the static stadium + track. Called every frame because the canvas
 * is cleared each tick (cheap on Canvas2D for 1920x1080). The "static" name
 * means it doesn't depend on horse state — only `layout`.
 */
export function drawStaticTrack(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  drawStadiumBackground(ctx);
  drawTrack(ctx, l);
}

function drawStadiumBackground(ctx: CanvasRenderingContext2D): void {
  const STAGE_W = 1920;
  const STAGE_H = 1080;
  // Sky gradient.
  const sky = ctx.createLinearGradient(0, 0, 0, STAGE_H * 0.45);
  sky.addColorStop(0, '#0d1530');
  sky.addColorStop(1, '#3b3450');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, STAGE_W, STAGE_H * 0.45);

  // Horizon glow (ambient stadium lights).
  const glow = ctx.createRadialGradient(STAGE_W / 2, STAGE_H * 0.42, 80, STAGE_W / 2, STAGE_H * 0.42, 900);
  glow.addColorStop(0, 'rgba(255, 196, 61, 0.18)');
  glow.addColorStop(1, 'rgba(255, 196, 61, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, STAGE_W, STAGE_H * 0.5);

  // Crowd silhouette (jagged horizon).
  ctx.fillStyle = '#1a1f3d';
  ctx.beginPath();
  ctx.moveTo(0, STAGE_H * 0.42);
  for (let x = 0; x <= STAGE_W; x += 24) {
    const noise = Math.sin(x * 0.07) * 4 + Math.sin(x * 0.17 + 1.3) * 6;
    ctx.lineTo(x, STAGE_H * 0.42 + noise);
  }
  ctx.lineTo(STAGE_W, STAGE_H * 0.5);
  ctx.lineTo(0, STAGE_H * 0.5);
  ctx.closePath();
  ctx.fill();

  // Crowd dot speckle (yellow + white pinpricks).
  ctx.save();
  ctx.globalAlpha = 0.5;
  for (let i = 0; i < 280; i++) {
    const x = (i * 137) % STAGE_W;
    const y = STAGE_H * 0.34 + ((i * 53) % 70);
    ctx.fillStyle = i % 5 === 0 ? '#FFC43D' : '#7d8aa6';
    ctx.fillRect(x, y, 2, 2);
  }
  ctx.restore();

  // Field gradient (below the horizon, behind the track).
  const field = ctx.createLinearGradient(0, STAGE_H * 0.48, 0, STAGE_H);
  field.addColorStop(0, '#0e2a1c');
  field.addColorStop(1, '#050f0a');
  ctx.fillStyle = field;
  ctx.fillRect(0, STAGE_H * 0.45, STAGE_W, STAGE_H * 0.55);
}

function drawTrack(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  const trackTop = l.trackY0 - 16;
  const trackBottom = l.trackY0 + l.laneH * l.numLanes + 16;
  const trackLeft = l.trackX0 - 40;
  const trackRight = l.trackX1 + 40;

  // Outer rail (dark wood).
  ctx.fillStyle = '#3d2415';
  roundedRect(ctx, { x: trackLeft - 8, y: trackTop - 8, w: trackRight - trackLeft + 16, h: trackBottom - trackTop + 16, r: 22 });
  ctx.fill();
  // Rail highlight.
  ctx.fillStyle = '#5b3a1f';
  roundedRect(ctx, { x: trackLeft - 4, y: trackTop - 4, w: trackRight - trackLeft + 8, h: trackBottom - trackTop + 8, r: 18 });
  ctx.fill();

  // Inner dirt base.
  const baseGrad = ctx.createLinearGradient(0, trackTop, 0, trackBottom);
  baseGrad.addColorStop(0, '#a3704a');
  baseGrad.addColorStop(0.5, '#8b5d3a');
  baseGrad.addColorStop(1, '#6e4528');
  ctx.fillStyle = baseGrad;
  roundedRect(ctx, { x: trackLeft, y: trackTop, w: trackRight - trackLeft, h: trackBottom - trackTop, r: 14 });
  ctx.fill();

  // Lane rows + dashed dividers + lane badges.
  drawLanes(ctx, l);
  drawDistanceMarkers(ctx, l);
  drawStartGate(ctx, l);
  drawFinishLine(ctx, l);
}

function drawLanes(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  const laneA = 'rgba(220, 175, 120, 0.32)';
  const laneB = 'rgba(180, 130, 85, 0.32)';
  for (let i = 0; i < l.numLanes; i++) {
    const yTop = l.trackY0 + i * l.laneH;
    ctx.fillStyle = i % 2 === 0 ? laneA : laneB;
    ctx.fillRect(l.trackX0, yTop, l.trackX1 - l.trackX0, l.laneH);
    // Subtle grain (pre-baked dot pattern).
    ctx.save();
    ctx.globalAlpha = 0.10;
    for (let g = 0; g < 26; g++) {
      const gx = l.trackX0 + ((g * 73 + i * 41) % (l.trackX1 - l.trackX0));
      const gy = yTop + ((g * 17) % l.laneH);
      ctx.fillStyle = '#3a2110';
      ctx.fillRect(gx, gy, 3, 2);
    }
    ctx.restore();

    if (i > 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = 2;
      ctx.setLineDash([18, 14]);
      ctx.beginPath();
      ctx.moveTo(l.trackX0, yTop);
      ctx.lineTo(l.trackX1, yTop);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    drawLaneBadge(ctx, l, i, yTop);
  }
}

function drawDistanceMarkers(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  const trackTop = l.trackY0 - 16;
  // 25%/50%/75% pole markers above the track.
  const fractions = [0.25, 0.5, 0.75];
  ctx.fillStyle = '#FFC43D';
  ctx.font = 'bold 16px Oswald, sans-serif';
  ctx.textAlign = 'center';
  for (const f of fractions) {
    const x = l.trackX0 + (l.trackX1 - l.trackX0) * f;
    ctx.fillRect(x - 1, trackTop - 18, 2, 18);
    ctx.fillStyle = '#FFC43D';
    ctx.fillText(`${Math.round(f * 100)}%`, x, trackTop - 22);
  }
}

function drawLaneBadge(ctx: CanvasRenderingContext2D, l: TrackLayout, i: number, yTop: number): void {
  const cx = l.trackX0 - 56;
  const cy = yTop + l.laneH / 2;
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  ctx.arc(cx, cy, 22, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,196,61,0.85)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#FFC43D';
  ctx.font = 'bold 28px Oswald, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(i + 1), cx, cy);
}

function drawStartGate(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  const gateW = 18;
  const gateY0 = l.trackY0 - 6;
  const gateY1 = l.trackY0 + l.laneH * l.numLanes + 6;
  // Gate frame.
  ctx.fillStyle = '#2a2a2a';
  ctx.fillRect(l.trackX0 - gateW, gateY0, gateW, gateY1 - gateY0);
  ctx.fillStyle = '#666';
  ctx.fillRect(l.trackX0 - gateW, gateY0, gateW, 2);
  ctx.fillRect(l.trackX0 - gateW, gateY1 - 2, gateW, 2);
  // Per-lane gate boxes.
  for (let i = 0; i < l.numLanes; i++) {
    const top = l.trackY0 + i * l.laneH + 6;
    const bot = l.trackY0 + (i + 1) * l.laneH - 6;
    ctx.fillStyle = '#444';
    ctx.fillRect(l.trackX0 - gateW + 3, top, gateW - 6, bot - top);
    ctx.fillStyle = '#FFC43D';
    ctx.font = 'bold 14px Oswald, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(i + 1), l.trackX0 - gateW / 2, (top + bot) / 2);
  }
  // START label rotated.
  ctx.fillStyle = '#FFC43D';
  ctx.font = 'bold 24px Oswald, sans-serif';
  ctx.textAlign = 'center';
  ctx.save();
  ctx.translate(l.trackX0 - gateW - 24, (gateY0 + gateY1) / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('START', 0, 0);
  ctx.restore();
}

function drawFinishLine(ctx: CanvasRenderingContext2D, l: TrackLayout): void {
  const finW = 30;
  const cellH = l.laneH / 4;
  // Checkered finish (per-lane 4-row to keep crisp at 1920x1080 scale).
  for (let lane = 0; lane < l.numLanes; lane++) {
    const yTop = l.trackY0 + lane * l.laneH;
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 2; col++) {
        const isWhite = (lane + row + col) % 2 === 0;
        ctx.fillStyle = isWhite ? '#fff' : '#111';
        ctx.fillRect(l.trackX1 - finW + col * (finW / 2), yTop + row * cellH, finW / 2, cellH);
      }
    }
  }
  // Finish flag pole + flag.
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(l.trackX1, l.trackY0 - 4);
  ctx.lineTo(l.trackX1, l.trackY0 - 70);
  ctx.stroke();
  ctx.fillStyle = '#dc2626';
  ctx.beginPath();
  ctx.moveTo(l.trackX1, l.trackY0 - 70);
  ctx.lineTo(l.trackX1 + 56, l.trackY0 - 58);
  ctx.lineTo(l.trackX1, l.trackY0 - 44);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#FFC43D';
  ctx.font = 'bold 22px Oswald, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('FINISH', l.trackX1 + 50, l.trackY0 - 78);
}

// ---------------------------------------------------------------------------
// Horse sprite (OpenArt pack: row 0 idle, row 4 run)
// ---------------------------------------------------------------------------

export interface DrawHorseArgs {
  h: TrackHorse;
  x: number;
  y: number;
  /** 0..1 phase value through the current animation cycle. */
  bobPhase: number;
  finished: boolean;
}

function frameFromPhase(phase: number, frameCount: number): number {
  const n = ((phase % 1) + 1) % 1;
  return Math.floor(n * frameCount) % frameCount;
}

// Source frame slot is 80x64. Upscale ~1.7× so the sprite covers roughly the
// same footprint as the procedural horse (HORSE_LEN/HORSE_THICK).
const SPRITE_SRC_W = 80;
const SPRITE_SRC_H = 64;
const SPRITE_DRAW_SCALE = 1.7;
const SPRITE_DRAW_W = SPRITE_SRC_W * SPRITE_DRAW_SCALE;
const SPRITE_DRAW_H = SPRITE_SRC_H * SPRITE_DRAW_SCALE;

// Procedural fallback uses a 4-frame cycle.
function procFrameFromPhase(phase: number): number {
  const n = ((phase % 1) + 1) % 1;
  return Math.floor(n * 4) % 4;
}

/**
 * Combined draw — kept for backward compatibility. Most callers now run a
 * two-pass loop (drawHorseBody for every horse, then drawLaneNumber for every
 * horse) so lane numbers always sit on top of every horse sprite — without
 * this split the next-lane horse body would cover the previous-lane number.
 */
export function drawHorse(ctx: CanvasRenderingContext2D, args: DrawHorseArgs): void {
  drawHorseBody(ctx, args);
  drawLaneNumber(ctx, args);
}

/** Body-only pass: shadow + sprite (or procedural fallback) + finish ring. */
export function drawHorseBody(ctx: CanvasRenderingContext2D, args: DrawHorseArgs): void {
  const { h, x, y, bobPhase, finished } = args;
  const anim: AnimName = finished ? 'idle' : 'run';
  const fc = animFrameCount(anim);
  const frameIdx = frameFromPhase(bobPhase, fc);
  const sprite = getSpriteFrame(h.number, anim, frameIdx);

  // Constant Y per lane — the sprite sheet's gallop frames already encode
  // body vertical motion, so a sin-driven bob doubled up against the per-frame
  // suspension drawing. Strict horizontal motion now.
  ctx.save();
  ctx.translate(x, y);

  // Drop shadow (constant size — bob removal eliminated the airtime scaling).
  ctx.fillStyle = 'rgba(0,0,0,0.42)';
  ctx.beginPath();
  ctx.ellipse(2, HORSE_THICK / 2 + 8, HORSE_LEN / 2 + 8, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  if (sprite) {
    // Pixel-art friendly: disable smoothing for the 64px source upscale.
    const prevSmooth = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      sprite.img,
      sprite.sx, sprite.sy, sprite.sw, sprite.sh,
      -SPRITE_DRAW_W / 2, -SPRITE_DRAW_H / 2 - 4,
      SPRITE_DRAW_W, SPRITE_DRAW_H,
    );
    ctx.imageSmoothingEnabled = prevSmooth;
  } else {
    drawProceduralHorse(ctx, h, procFrameFromPhase(bobPhase));
  }

  if (finished) {
    ctx.strokeStyle = 'rgba(255,196,61,0.95)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.ellipse(0, 0, HORSE_LEN / 2 + 14, HORSE_THICK / 2 + 16, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Lane-number-only pass: draws the badge at the same world position as the
 * body. Run AFTER all bodies so adjacent-lane sprites can never occlude the
 * number. Mirrors the body's bob/tilt so the badge tracks with the horse.
 */
export function drawLaneNumber(ctx: CanvasRenderingContext2D, args: DrawHorseArgs): void {
  const { h, x, y, bobPhase, finished } = args;
  // Procedural fallback path doesn't draw a badge today — keep parity with
  // the previous behaviour (badge only when the bitmap sprite is in use).
  const anim: AnimName = finished ? 'idle' : 'run';
  const fc = animFrameCount(anim);
  const frameIdx = frameFromPhase(bobPhase, fc);
  const sprite = getSpriteFrame(h.number, anim, frameIdx);
  if (!sprite) return;

  // Mirror the body pass — constant Y per lane, sprite frames carry motion.
  ctx.save();
  ctx.translate(x, y);
  drawLaneNumberBadge(ctx, h);
  ctx.restore();
}

function drawLaneNumberBadge(ctx: CanvasRenderingContext2D, h: TrackHorse): void {
  const badgeY = -SPRITE_DRAW_H / 2 - 14;
  ctx.save();
  ctx.fillStyle = h.silkColor;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, badgeY, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 14px Oswald, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = 3;
  ctx.fillText(String(h.number), 0, badgeY + 1);
  ctx.restore();
}
